import React, { useEffect, useState, useCallback } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'qrcode';
import { referralApi } from '@/api/referral';

/**
 * 全局分享浮窗（v2.0.148）
 *
 * 挂在 MainLayout 内，仅登录用户可见。点击右下角按钮弹出对话框：
 *  - 把"当前页面 URL"注入分享人推广码（?invite=CODE），生成二维码 + 可复制链接；
 *  - 好友扫码/打开后，落地时由 referralCapture 兜底捕获，注册即绑定为分享人的推广客户；
 *  - 支持下载 PNG / SVG 二维码。
 *
 * 这样无论分享哪个页面，链接与二维码都自动带上分享人的 id（推广码）。
 */
const ShareFloatButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState(false);
  const [qrSrc, setQrSrc] = useState('');
  const [snack, setSnack] = useState<{ severity: 'success' | 'error' | 'info'; message: string } | null>(null);

  /** 当前页面 URL，注入分享人推广码 */
  const shareLink = React.useMemo(() => {
    if (!code) return '';
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('invite', code);
      return url.toString();
    } catch {
      return '';
    }
  }, [code]);

  // 打开时懒加载当前用户推广码
  const loadCode = useCallback(async () => {
    if (code || codeLoading) return;
    setCodeLoading(true);
    setCodeError(false);
    try {
      const r = await referralApi.getMyInviteCode();
      setCode(r.inviteCode || '');
    } catch {
      setCodeError(true);
    } finally {
      setCodeLoading(false);
    }
  }, [code, codeLoading]);

  useEffect(() => {
    if (open) {
      loadCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 推广码就绪后生成二维码
  useEffect(() => {
    if (!shareLink) return;
    let cancelled = false;
    QRCode.toDataURL(shareLink, { width: 220, margin: 1, errorCorrectionLevel: 'M' })
      .then((d) => {
        if (!cancelled) setQrSrc(d);
      })
      .catch(() => {
        if (!cancelled) setQrSrc('');
      });
    return () => {
      cancelled = true;
    };
  }, [shareLink]);

  const copyText = useCallback(async (text: string, okMsg: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ severity: 'success', message: okMsg });
    } catch {
      setSnack({ severity: 'error', message: '复制失败，请手动复制' });
    }
  }, []);

  const downloadPng = useCallback(() => {
    if (!shareLink || !code) return;
    QRCode.toDataURL(shareLink, { width: 480, margin: 1, errorCorrectionLevel: 'M' }).then((d) => {
      const a = document.createElement('a');
      a.href = d;
      a.download = `ibusiness-share-${code}.png`;
      a.click();
    });
  }, [shareLink, code]);

  const downloadSvg = useCallback(() => {
    if (!shareLink || !code) return;
    QRCode.toString(shareLink, { type: 'svg', margin: 1, width: 480 }).then((svg) => {
      const blob = new Blob([svg as string], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ibusiness-share-${code}.svg`;
      a.click();
      URL.revokeObjectURL(url);
    });
  }, [shareLink, code]);

  return (
    <>
      <Tooltip title="分享当前页面" placement="left">
        <Fab
          color="primary"
          aria-label="分享当前页面"
          onClick={() => setOpen(true)}
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
        >
          <ShareIcon />
        </Fab>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 6 }}>
          分享当前页面
          <IconButton
            aria-label="关闭"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            好友通过你的链接或二维码注册，将自动绑定为你的推广客户（分润归属你）。
          </Typography>

          {codeLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={28} />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                正在获取你的推广码…
              </Typography>
            </Box>
          ) : codeError ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="error">
                获取推广码失败，请稍后重试
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                {qrSrc ? (
                  <img
                    src={qrSrc}
                    width={220}
                    height={220}
                    alt="分享二维码"
                    style={{ borderRadius: 8, border: '1px solid #eee' }}
                  />
                ) : (
                  <CircularProgress size={24} />
                )}
              </Box>

              <TextField
                value={shareLink}
                fullWidth
                size="small"
                InputProps={{ readOnly: true }}
                sx={{ mb: 1.5 }}
                helperText="已自动带上你的分享人 ID（推广码）"
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyText(shareLink, '链接已复制')}
                  disabled={!shareLink}
                >
                  复制链接
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadPng} disabled={!shareLink}>
                  下载 PNG
                </Button>
                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadSvg} disabled={!shareLink}>
                  下载 SVG
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>关闭</Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Box
          role="alert"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1400,
            backgroundColor: snack.severity === 'error' ? '#d32f2f' : snack.severity === 'info' ? '#1976d2' : '#2e7d32',
            color: '#fff',
            px: 2,
            py: 1,
            borderRadius: 1,
            fontSize: 13,
            boxShadow: 3,
          }}
          onClick={() => setSnack(null)}
        >
          {snack.message}
        </Box>
      )}
    </>
  );
};

export default ShareFloatButton;
